import { callHostedTool, ensureActorForUser } from './actor-service.js'
import { buildHostedConfigStatus } from '@hormigadormida/shared-runtime/hosted-config.js'
import { t, escapeHtml } from './portal-utils.js'
import {
  renderPortalSkeleton,
  renderMigaState,
  renderMigaMeta,
  renderBlockedPortal,
  renderJoinPortal,
  renderInviteResult
} from './portal-renderers.js'
import { copyToClipboard, resolveWorkspaceIds } from './portal-services.js'
import { setSafeHtml } from './safe-html.js'
import {
  isSeededFixtureWorkspace,
  readSeededWorkspaceFromStorage,
  writeSeededQueryToStorage,
  buildSeededFixtureHostedConfig,
  buildSeededFixtureIntelligence,
  redeemWorkspaceInviteWithFixture,
} from './portal-seeded-fixtures.js'
import { createWorkspaceSubscriptions } from './portal-workspace-subscriptions.js'

/**
 * @param {{ user?: any, root?: HTMLElement | null }} [options]
 */
export async function mountOperatorPortal({ user, root }: { user?: any, root?: HTMLElement | null } = {}) {
  if (!user || !root) {
    return () => {}
  }

  const localHostedConfig = buildHostedConfigStatus({
    checks: [
      {
        key: 'VITE_FUNCTIONS_MCP_URL',
        present: Boolean(t(import.meta.env.VITE_FUNCTIONS_MCP_URL)),
        requiredForRead: true,
        requiredForWrite: true,
        guidance: 'Add VITE_FUNCTIONS_MCP_URL to the web env so the portal can reach the hosted operator function.'
      }
    ]
  })
  if (!localHostedConfig.canRead) {
    setSafeHtml(root, renderBlockedPortal(localHostedConfig))
    return () => {}
  }

  const actor = await ensureActorForUser(user)
  let workspaceIds = await resolveWorkspaceIds(user, actor)
  const seededWorkspaceFromStorage = readSeededWorkspaceFromStorage()
  if (seededWorkspaceFromStorage && !workspaceIds.includes(seededWorkspaceFromStorage)) {
    workspaceIds = [seededWorkspaceFromStorage, ...workspaceIds]
  }
  const inviteCodeFromUrl = new URLSearchParams(window.location.search).get('invite') ?? ''
  if (workspaceIds.length === 0) {
    if (inviteCodeFromUrl) {
      try {
        await redeemWorkspaceInviteWithFixture(/** @type {any} */ ({ user, inviteCode: inviteCodeFromUrl }))
        return mountOperatorPortal({ user, root })
      } catch (inviteError) {
        setSafeHtml(root, renderJoinPortal({
          inviteCode: inviteCodeFromUrl,
          error: inviteError instanceof Error ? inviteError.message : String(inviteError)
        }))
      }
    }
    const bindJoinFlow = (inviteCode = '', error = '') => {
      setSafeHtml(root, renderJoinPortal({ inviteCode, error }))
      const joinForm = root.querySelector('#op-join-form')
      const joinInput = root.querySelector<HTMLInputElement>('#op-join-code')
      const submitButton = root.querySelector('#op-join-submit')
      joinForm?.addEventListener('submit', async (event) => {
        event.preventDefault()
        const nextInviteCode = t(joinInput?.value)
        if (!nextInviteCode) return
        submitButton?.setAttribute('disabled', 'true')
        try {
          await redeemWorkspaceInviteWithFixture(/** @type {any} */ ({ user, inviteCode: nextInviteCode }))
          await mountOperatorPortal({ user, root })
        } catch (joinError) {
          bindJoinFlow(
            nextInviteCode,
            joinError instanceof Error ? joinError.message : String(joinError)
          )
        } finally {
          submitButton?.removeAttribute('disabled')
        }
      })
    }
    bindJoinFlow(inviteCodeFromUrl)
    return () => {}
  }

  let hostedConfig = await callHostedTool('getHostedConfigStatus', { workspaceId: workspaceIds[0] }).catch(() => ({
    status: 'blocked',
    missing: ['hosted_control_plane'],
    guidance: ['The hosted operator function is not reachable or is misconfigured.'],
    canRead: false,
    canWrite: false
  }))
  if (isSeededFixtureWorkspace(workspaceIds[0])) {
    hostedConfig = {
      ...buildSeededFixtureHostedConfig(),
      ...hostedConfig,
      canRead: true
    }
  }
  if (!hostedConfig.canRead) {
    setSafeHtml(root, renderBlockedPortal(hostedConfig))
    return () => {}
  }

  setSafeHtml(root, renderPortalSkeleton())

  const workspaceSelect = root.querySelector<HTMLSelectElement>('#operator-workspace-select')!
  const invitePanel = root.querySelector('#op-invite-panel')
  const inviteToggle = root.querySelector('#op-invite-toggle')
  const inviteClose = root.querySelector('#op-invite-close')
  const inviteCreate = root.querySelector('#op-invite-create')
  const inviteFeedback = root.querySelector<HTMLElement>('#op-invite-feedback')!
  const inviteRole = root.querySelector<HTMLSelectElement>('#op-invite-role')!
  const inviteExpiry = root.querySelector<HTMLSelectElement>('#op-invite-expiry')!
  const inviteUses = root.querySelector<HTMLSelectElement>('#op-invite-uses')!
  const migaForm = root.querySelector('#op-miga-form')
  const migaQuestion = root.querySelector<HTMLTextAreaElement>('#op-miga-question')!
  const migaOutput = root.querySelector<HTMLElement>('#op-miga-output')!
  const migaMeta = root.querySelector<HTMLElement>('#op-miga-meta')!

  const preferredWorkspace = new URLSearchParams(window.location.search).get('workspace') ?? ''
  setSafeHtml(
    workspaceSelect,
    workspaceIds.length
      ? workspaceIds.map((workspaceId) => `<option value="${escapeHtml(workspaceId)}">${escapeHtml(workspaceId)}</option>`).join('')
      : '<option value="">No workspaces linked yet</option>'
  )
  if (preferredWorkspace && workspaceIds.includes(preferredWorkspace)) {
    workspaceSelect.value = preferredWorkspace
  }

  const { unsubscribe, bindWorkspace } = createWorkspaceSubscriptions(root, {
    workspaceSelect,
    migaOutput,
    migaMeta,
  })

  const openInvitePanel = () => {
    invitePanel?.classList.remove('hidden')
  }

  const closeInvitePanel = () => {
    invitePanel?.classList.add('hidden')
  }

  inviteToggle?.addEventListener('click', openInvitePanel)
  inviteClose?.addEventListener('click', closeInvitePanel)

  inviteCreate?.addEventListener('click', async () => {
    const workspaceId = t(workspaceSelect?.value)
    if (!workspaceId) return
    inviteCreate.setAttribute('disabled', 'true')
    if (inviteFeedback) inviteFeedback.textContent = ''
    try {
      const invite = await callHostedTool('createWorkspaceInvite', {
        workspaceId,
        role: t(inviteRole?.value) || 'operator',
        expiresInMs: Number(inviteExpiry?.value || 604800000),
        maxUses: Number(inviteUses?.value || 1)
      })
      setSafeHtml(inviteFeedback, renderInviteResult(invite))
      inviteFeedback.querySelectorAll('[data-op-copy]').forEach((button) => {
        button.addEventListener('click', async (event) => {
          const target = event.currentTarget as HTMLElement
          const value = target.getAttribute('data-op-copy')
          if (!value) return
          const result = await copyToClipboard(value)
          if (result.success) {
            target.textContent = 'Copied'
            setTimeout(() => {
              target.textContent = t(value).includes('http') ? 'Copy link' : 'Copy code'
            }, 1200)
          } else {
            // VAL-WEB-020: Show visible copy failure feedback and restore label
            target.textContent = 'Copy failed'
            setTimeout(() => {
              target.textContent = t(value).includes('http') ? 'Copy link' : 'Copy code'
            }, 1500)
          }
        })
      })
    } catch (error) {
      setSafeHtml(inviteFeedback, `<div class="op-alert">${escapeHtml(error instanceof Error ? error.message : String(error))}</div>`)
    } finally {
      inviteCreate.removeAttribute('disabled')
    }
  })

  migaForm?.addEventListener('submit', async (event) => {
    event.preventDefault()
    const workspaceId = t(workspaceSelect?.value)
    const question = t(migaQuestion?.value)
    if (!workspaceId || !question) return
    const submitButton = /** @type {HTMLButtonElement | null} */ (root.querySelector('#op-miga-submit'))
    submitButton?.setAttribute('disabled', 'true')
    migaOutput.textContent = 'Miga is grounding that answer...'
    migaMeta.textContent = 'Checking cached workspace intelligence and escalating to the operator engine if a fresh read is required.'
    try {
      const response = await callHostedTool('queryOperatorIntelligence', {
        workspaceId,
        question,
        context: {
          type: 'general',
          summary: 'Hosted portal intelligence query.'
        }
      })
      const intelligence = response?.intelligence || {}
      if (isSeededFixtureWorkspace(workspaceId)) {
        writeSeededQueryToStorage(workspaceId, intelligence)
      }
      setSafeHtml(migaOutput, renderMigaState(intelligence))
      migaMeta.textContent = renderMigaMeta(intelligence)
    } catch (error) {
      if (isSeededFixtureWorkspace(workspaceId)) {
        const fallbackIntelligence = buildSeededFixtureIntelligence(
          question,
          error instanceof Error ? error.message : String(error)
        )
        writeSeededQueryToStorage(workspaceId, fallbackIntelligence)
        setSafeHtml(migaOutput, renderMigaState(fallbackIntelligence))
        migaMeta.textContent = renderMigaMeta(fallbackIntelligence)
      } else {
        migaOutput.textContent = error instanceof Error ? error.message : String(error)
        migaMeta.textContent = 'Hosted intelligence query failed.'
      }
    } finally {
      submitButton?.removeAttribute('disabled')
    }
  })

  workspaceSelect.addEventListener('change', () => {
    const url = new URL(window.location.href)
    if (t(workspaceSelect.value)) {
      url.searchParams.set('workspace', t(workspaceSelect.value))
    } else {
      url.searchParams.delete('workspace')
    }
    history.replaceState({}, '', url.toString())
    bindWorkspace(t(workspaceSelect.value))
  })

  bindWorkspace(t(workspaceSelect.value))

  return () => {
    unsubscribe()
  }
}
